import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, MessageSquareQuote, RotateCcw, Shield, Loader2, History } from "lucide-react";
import { VerificationBadge } from "@/components/VerificationBadge";

const ACTIONS = {
  testimony_confirmed: { icon: MessageSquareQuote, hint: "Nécessite au moins un témoignage oral avec le nom du témoin." },
  cross_validated: { icon: CheckCircle2, hint: "Nécessite au moins deux sources concordantes." },
  to_verify: { icon: RotateCcw, hint: "Indiquez la raison (doute, information contradictoire…)." },
};

const LegacyNote = ({ legacy }) => {
  if (!legacy || legacy.status !== "verified") return null;
  return (
    <p className="mt-3 text-xs text-[#6E5B4F] flex items-center gap-1" data-testid="legacy-verification">
      <History className="w-3 h-3" /> Anciennement « vérifiée »{legacy.by_name ? ` par ${legacy.by_name}` : ""}{legacy.at ? ` le ${legacy.at.slice(0, 10)}` : ""} (avant la mise à jour des statuts).
    </p>
  );
};

export const VerificationControls = ({ person, onChanged }) => {
  const { API, user } = useAuth();
  const { t } = useLang();
  const [action, setAction] = useState(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const status = person.verification_status || "to_verify";
  const isElder = user?.role === "elder" || user?.role === "admin" || user?.is_super_admin;
  const available = Object.keys(ACTIONS).filter(s => s !== status && (isElder || s === "to_verify"));

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/persons/${person.id}/verify`, { status: action, notes });
      toast.success(t("statusChanged"));
      setAction(null); setNotes("");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <div className="heritage-card p-4" data-testid="verification-controls">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <VerificationBadge status={status} size="lg" />
          {status !== "to_verify" && person.verified_by_name && (
            <div className="text-xs text-[#6E5B4F]">
              <Shield className="w-3 h-3 inline mr-1" />
              Statut attribué par <span className="font-semibold text-[#2C4230]">{person.verified_by_name}</span> {t("on")} {person.verified_at?.slice(0, 10)}
            </div>
          )}
        </div>
        {user && (
          <div className="flex flex-wrap items-center gap-2">
            {available.map(s => {
              const Icon = ACTIONS[s].icon;
              return (
                <Button key={s} size="sm" variant={s === "to_verify" ? "outline" : "default"}
                  className={s === "to_verify" ? "border-[#A96B18] text-[#A96B18]" : "btn-terra"}
                  onClick={() => { setNotes(""); setAction(s); }} data-testid={`set-status-${s}`}>
                  <Icon className="w-4 h-4 mr-2" /> {s === "to_verify" ? "Remettre « À vérifier »" : t(s)}
                </Button>
              );
            })}
          </div>
        )}
      </div>
      {person.verification_notes && (
        <p className="mt-3 text-sm italic text-[#6E5B4F] border-l-2 border-[#D9822B] pl-3" data-testid="verification-notes-display">
          « {person.verification_notes} »
        </p>
      )}
      <LegacyNote legacy={person.legacy_verification} />
      {!isElder && <p className="mt-3 text-xs text-[#6E5B4F]">{t("editAutoDowngrade")}</p>}

      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="bg-[#FAF7F2]">
          <DialogHeader>
            <DialogTitle className="font-serif-heritage text-2xl text-[#261911]">{action && t(action)}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6E5B4F]">{action && ACTIONS[action].hint}</p>
          <Label>{t("verificationNotes")}</Label>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="status-notes-input" />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setAction(null)}>{t("cancel")}</Button>
            <Button className="btn-terra" onClick={submit} disabled={loading || (action === "to_verify" && !notes.trim())} data-testid="confirm-status-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirmer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VerificationControls;
